import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { parseNonNegativeInt } from '@/components/inventory/parseNonNegativeInt';
import { DEFAULT_LOW_STOCK_THRESHOLD } from '@/supabase/inventory/types/inventory';
import type { UnmatchedOrderItem } from '@/supabase/inventory/types/inventory';

export interface RegisterUnmatchedItemDialogHandle {
  open: (item: UnmatchedOrderItem) => void;
}

interface RegisterUnmatchedItemDialogContainerProps {
  // 제출 로직(pending 가드·createItem·실패 재동기화)은 Inventory 페이지 핸들러가 소유 —
  // 다이얼로그는 열림 상태 + 현재고·부족 기준 입력만 관리한다(도메인 핸들러를 컨테이너에
  // 복제하지 않는다, CLAUDE.md 규율 1). 성공 여부를 돌려받아 닫힘만 결정.
  onSubmit: (item: UnmatchedOrderItem, initialStock: number, lowStockThreshold: number) => Promise<boolean>;
  isSubmitting: boolean;
}

const DEFAULT_STOCK = '0';
// 부족 알림 기준 디폴트 — 도메인 상수(types/inventory)에서 파생해 신규 등록 모달과 값 드리프트
// 불가(codexloop R1 #5 수용). 이전엔 입력란 없이 Inventory.tsx가 5를 하드코딩했는데
// (UNMATCHED_REGISTER_DEFAULT_THRESHOLD), 입력란이 생기며 디폴트가 입력 초기값으로 이동했다.
const DEFAULT_THRESHOLD = String(DEFAULT_LOW_STOCK_THRESHOLD);

/**
 * 미매칭 주문 상품 '재고로 등록' 다이얼로그(오너 피드백 2026-07-13). 원클릭 즉시 등록이던
 * 흐름에 현재고 입력 단계를 넣는다 — 지금 재고를 아는 셀러는 바로 입력하고, 모르면 0으로
 * 등록해 두고 나중에 '조정'으로 맞추면 된다는 안내를 함께 노출한다.
 *
 * 대상 상품은 `open(item)` 인자로만 시딩되고 imperative handle은 `open`만 노출 → 부모는
 * ref 호출만 하므로 다이얼로그 오픈 시 부모가 재렌더되지 않는다.
 *
 * 실패 시(중복 등록 등 — 에러 토스트는 createItem 내부 처리) 열린 채 유지해 재시도를
 * 허용한다(DeleteItemDialogContainer와 동일 계약). 제출 중에는 ESC/오버레이/X 닫힘도
 * 거부한다(isPendingRef 기준) — 세션 가드는 닫힌 UI를 응답이 못 건드리게 할 뿐 진행 중인
 * 쓰기를 취소하지 않으므로, 닫힘을 허용하면 "취소했는데 백그라운드에서 등록 성공"이
 * 생긴다(codexloop 등록다이얼로그 R1 수용). 중복 케이스는 부모의 실패 재동기화(refresh)가
 * 패널 행을 이미 제거하므로 수동 닫기만 하면 된다.
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화(중복 제출 가드)를
 *    실시간으로 반영해야 하는 부모 live prop이다. 다른 모달 컨테이너 선례와 동일하게
 *    "open-state를 내부에 격리한 컨테이너는 항상 memo 금지"로 통일.
 */
export const RegisterUnmatchedItemDialogContainer = forwardRef<
  RegisterUnmatchedItemDialogHandle,
  RegisterUnmatchedItemDialogContainerProps
>(({ onSubmit, isSubmitting }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState<UnmatchedOrderItem | null>(null);
  const [stock, setStock] = useState(DEFAULT_STOCK);
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD);
  // 제출 중 표시(입력·버튼 비활성)용 state + 닫힘 거부/더블서밋 동기 가드용 ref 쌍 —
  // state만으로는 클릭과 리렌더 사이 구간에 가드가 안 걸린다(registeringNorms 쌍과 동일 이유).
  const [isPending, setIsPending] = useState(false);
  const isPendingRef = useRef(false);
  // 진행 중이던 제출(await)이 끝나기 전에 다이얼로그가 닫혔다가 다른 대상으로
  // 재오픈되면, 그 응답이 새 세션의 상태를 건드리지 않도록 세대를 구분한다.
  const sessionRef = useRef(0);

  const open = useCallback<RegisterUnmatchedItemDialogHandle['open']>((nextItem) => {
    sessionRef.current += 1;
    setItem(nextItem);
    setStock(DEFAULT_STOCK);
    setThreshold(DEFAULT_THRESHOLD);
    setIsOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ open }), [open]);

  const handleOpenChange = useCallback((next: boolean) => {
    // 제출 중 닫힘(ESC/오버레이/X) 거부 — 진행 중인 등록을 취소한 것처럼 보이게 두지 않는다.
    if (!next && isPendingRef.current) return;
    if (!next) {
      sessionRef.current += 1;
      setItem(null);
      setStock(DEFAULT_STOCK);
      setThreshold(DEFAULT_THRESHOLD);
    }
    setIsOpen(next);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!item || isPendingRef.current) return;
    const parsedStock = parseNonNegativeInt(stock);
    const parsedThreshold = parseNonNegativeInt(threshold);
    if (parsedStock === null || parsedThreshold === null) return;
    isPendingRef.current = true;
    setIsPending(true);
    const session = sessionRef.current;
    try {
      const success = await onSubmit(item, parsedStock, parsedThreshold);
      if (session !== sessionRef.current) return;
      if (success) {
        setItem(null);
        setStock(DEFAULT_STOCK);
        setThreshold(DEFAULT_THRESHOLD);
        setIsOpen(false);
      }
    } finally {
      isPendingRef.current = false;
      setIsPending(false);
    }
  }, [item, stock, threshold, onSubmit]);

  const isStockValid = parseNonNegativeInt(stock) !== null;
  const isThresholdValid = parseNonNegativeInt(threshold) !== null;
  const isBusy = isSubmitting || isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="break-keep">'{item?.name ?? ''}' 재고로 등록</DialogTitle>
          <DialogDescription className="break-keep">
            현재 보유 수량을 입력해 주세요. 정확한 수량을 모르면 0으로 등록한 뒤 나중에
            '조정'으로 맞출 수 있습니다.
          </DialogDescription>
        </DialogHeader>
        {/* 단일 입력 다이얼로그라 Enter 제출이 자연스럽다(데스크톱 퍼스트) — form 기본 제출로
            연결하고 취소는 type=button으로 제출에서 제외. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <div className="space-y-3 py-2">
            {/* 신규 등록 모달과 동일한 숫자 필드 grid 배치(어휘·레이아웃 일치 — 같은 개념은
                어느 경로에서든 같은 모습). 부족 기준은 등록 시점에 함께 정할 수 있게 노출
                (오너 피드백 2026-07-16 — 이전엔 5 하드코딩). */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="register-unmatched-stock">현재 재고</Label>
                <Input
                  id="register-unmatched-stock"
                  type="number"
                  min={0}
                  step={1}
                  autoFocus
                  disabled={isBusy}
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="register-unmatched-threshold">부족 알림 기준</Label>
                <Input
                  id="register-unmatched-threshold"
                  type="number"
                  min={0}
                  step={1}
                  disabled={isBusy}
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                />
              </div>
            </div>
            <p className="break-keep text-xs text-slate-400">
              등록하면 상품명이 같은 주문(정산서)과 자동으로 매칭됩니다. 입금 전 주문은 바로
              예약에 반영되지만, 이미 입금 완료된 주문은 소급 차감되지 않습니다.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
              취소
            </Button>
            <Button
              type="submit"
              className="bg-slate-900 text-white hover:bg-slate-800"
              disabled={isBusy || !item || !isStockValid || !isThresholdValid}
            >
              등록
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});

RegisterUnmatchedItemDialogContainer.displayName = 'RegisterUnmatchedItemDialogContainer';
